import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useAvailableRarities } from '@sorare/core/src/hooks/my-cards/useAvailableRarities.tsx';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';

import { MyCardsRaritySwitcherBox } from '#src/components/myCards/MyCardsRaritySwitcherBox/index.tsx';

import type { CardOrCollectionRaritySwitcher_user } from './__generated__/index.graphql.ts';

const StyledTabBar = styled(TabBar)`
  margin: 0 var(--unit);
`;

type Props = {
  rarityPath: (rarity: Rarity) => string;
  type: Rarity;
  onClick?: (rarity: Rarity) => void;
  user: Nullable<CardOrCollectionRaritySwitcher_user>;
  forceShowCommon?: boolean;
  sealed?: boolean;
  matches?: (rarity: Rarity) => ComponentProps<typeof Tab>['matches'];
};

const getDisabled = (
  rarity: Rarity,
  selectionMode: string | undefined,
  sealed: boolean
) => {
  const isCommon = rarity === Rarity.common;
  return isCommon && (selectionMode === 'sealing' || sealed);
};

export const CardOrCollectionRaritySwitcher = ({
  rarityPath,
  type,
  onClick,
  user,
  forceShowCommon,
  sealed = false,
  matches,
}: Props) => {
  const availableRarities = useAvailableRarities();
  const isLaptopAndAbove = useIsLaptop();
  const { cardCounts } = user || {};
  const seriesNav = useSeriesNav();
  const selectionMode = useQueryString('selectionMode');

  if (seriesNav) {
    return null;
  }
  const cardsCountByRarity: Record<Rarity, number> = {
    [Rarity.common]: cardCounts?.common || 0,
    [Rarity.limited]: cardCounts?.limited || 0,
    [Rarity.rare]: cardCounts?.rare || 0,
    [Rarity.super_rare]: cardCounts?.superRare || 0,
    [Rarity.unique]: cardCounts?.unique || 0,
    // Not displayed but here only for type
    [Rarity.custom_series]: 0,
  };

  const cardsCountByAvailableRarity: Record<Rarity, number> =
    availableRarities.reduce(
      (acc, rarity) => {
        return {
          ...acc,
          [rarity]: cardsCountByRarity[rarity as Rarity],
        };
      },
      {} as Record<Rarity, number>
    );

  const showLimitedByDefault =
    !cardCounts?.rare &&
    !cardCounts?.superRare &&
    !cardCounts?.unique &&
    cardCounts?.limited;

  const displayedRarities = Object.entries(cardsCountByAvailableRarity).filter(
    ([rarity, value]) => {
      return (
        !!value ||
        (forceShowCommon && rarity === Rarity.common) ||
        (showLimitedByDefault && rarity === Rarity.limited)
      );
    }
  );

  if (!isLaptopAndAbove) {
    return (
      <StyledTabBar scrollable>
        {displayedRarities.map(([key, value]) => {
          const rarity = key as Rarity;
          const disabled = getDisabled(rarity, selectionMode, sealed);
          const active = type === rarity;
          return (
            <Tab
              key={key}
              disabled={disabled}
              to={rarityPath(rarity)}
              onClick={onClick ? () => onClick(rarity) : undefined}
              className={classNames({ active, disabled })}
              matches={matches?.(rarity)}
            >
              <ScarcityIcon rarity={rarity} size={2} />
              <LabelL bold>{value}</LabelL>
            </Tab>
          );
        })}
      </StyledTabBar>
    );
  }

  return (
    <Horizontal>
      {displayedRarities.map(([key, value]) => {
        const rarity = key as Rarity;
        const disabled = getDisabled(rarity, selectionMode, sealed);
        const active = type === rarity;

        return (
          <MyCardsRaritySwitcherBox
            key={key}
            as={disabled ? 'button' : undefined}
            role={disabled ? 'button' : undefined}
            disabled={disabled}
            to={rarityPath(rarity)}
            keepSearchParams
            onClick={onClick ? () => onClick(rarity) : undefined}
            className={classNames({ active, disabled })}
          >
            <ScarcityIcon rarity={rarity} size={5} />
            <LabelM bold>{value}</LabelM>
          </MyCardsRaritySwitcherBox>
        );
      })}
    </Horizontal>
  );
};

CardOrCollectionRaritySwitcher.fragments = {
  user: gql`
    fragment CardOrCollectionRaritySwitcher_user on PublicUserInfoInterface {
      id
      slug
      cardCounts(sport: $sport) {
        common
        limited
        rare
        superRare
        unique
      }
    }
  ` as TypedDocumentNode<CardOrCollectionRaritySwitcher_user>,
};
